// A user without a photo (60.11.599): a figure that says something, never the first letters of their name. The user,
// 2026-10-27: «آواتارهای پنل وب برای کاربران هم معنا دار و درست کن و اول اسمشون رو نذار».
// - who: a man in a shirt and tie or a woman with shoulder-length hair only when the name is kept as «آقای …» / «خانم …»;
//   the panel keeps no gender, so anyone else is a plain figure - never guessed from a first name. No religious sign of
//   any kind, on anyone.
// - what: the colour and the badge of their access, the same signs «کاربران و دسترسی‌ها» shows beside each role
//   (companyUserRules ROLE_OPTIONS): «دسترسی کامل» a crown, «ویرایش و ارسال صورتحساب» a paper plane, «ایجاد صورتحساب» a
//   document, «فقط مشاهده» an eye; the system's administrator a shield, a partner (API) a handshake, a call-center agent a
//   headset. A custom access or an unknown one: no badge.
// The same family of drawings as the CRM's figures (CallLog_Figures_Helper), so a colleague looks alike in both.

export type FigureGender = 'male' | 'female' | ''
export type FigureKind = 'full' | 'send' | 'create' | 'view' | 'system' | 'partner' | 'agent' | ''

/** The access of a business user (CompanyUser.role) or the signed-in user's level (AuthModel.rl). */
export const figureKindOfRole = (role?: number | string | null): FigureKind => {
  if (role === null || role === undefined || role === '') return ''
  switch (Number(role)) {
    case 0:
      return 'full'
    case 100:
      return 'send'
    case 101:
      return 'create'
    case 102:
      return 'view'
    // The token's levels: 1 and 10 the system's administrators, 20 a partner's (TokenController.makeAuthUser).
    case 1:
    case 10:
      return 'system'
    case 20:
      return 'partner'
    default:
      return ''
  }
}

const words = (name?: string | null) =>
  String(name || '')
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/‌/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

/** «خانم …» a woman's figure, «آقای …» a man's; any other name says nothing (never a guess from a first name). */
export const genderOfTitle = (name?: string | null): FigureGender => {
  const value = words(name)
  if (/^(سرکار )?خانم( |$)/.test(value) || /^(mrs|ms|miss)\.?( |$)/.test(value)) return 'female'
  if (/^(جناب )?آقا(ی)?( |$)/.test(value) || /^mr\.?( |$)/.test(value)) return 'male'
  return ''
}

const KINDS: Record<Exclude<FigureKind, ''>, {colour: string; title: string; badge: string}> = {
  full: {
    colour: '#1d3557',
    title: 'دسترسی کامل',
    badge:
      '<path d="M4.2 8.2l3.9 3.2L12 5.4l3.9 6 3.9-3.2-1.6 9.4H5.8z" fill="{c}"/><rect x="5.8" y="18" width="12.4" height="2.2" rx="1" fill="{c}"/>',
  },
  send: {
    colour: '#0077c8',
    title: 'ویرایش و ارسال صورتحساب',
    badge:
      '<path d="M20.4 3.8L3.6 10.6l6.3 2.4 2.4 6.4z" fill="{c}"/><path d="M9.9 13l10.5-9.2" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/>',
  },
  create: {
    colour: '#16a36a',
    title: 'ایجاد صورتحساب',
    badge:
      '<path d="M6.2 3.4h8.2l4 4v12.2a1 1 0 0 1-1 1H6.2a1 1 0 0 1-1-1V4.4a1 1 0 0 1 1-1z" fill="{c}"/><path d="M8.4 12h7.2M8.4 15.4h7.2M8.4 8.6h3.6" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>',
  },
  view: {
    colour: '#64748b',
    title: 'فقط مشاهده',
    badge:
      '<path d="M2.6 12c2.2-4 5.4-6 9.4-6s7.2 2 9.4 6c-2.2 4-5.4 6-9.4 6s-7.2-2-9.4-6z" fill="{c}"/><circle cx="12" cy="12" r="3.2" fill="#fff"/>',
  },
  system: {
    colour: '#7a4cc2',
    title: 'مدیر سیستم',
    badge:
      '<path d="M12 3.4l7.2 2.8v5.5c0 4.4-3 7.8-7.2 9.1-4.2-1.3-7.2-4.7-7.2-9.1V6.2z" fill="{c}"/><path d="M8.5 12.2l2.5 2.5 4.6-4.9" fill="none" stroke="#fff" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  partner: {
    colour: '#0e9aa7',
    title: 'همکار API',
    badge:
      '<path d="M3.4 9.6l3.8-3 3.4 2.2 2.8-1.6 3.4.4 3.8 3.4-2.6 3.8-4.6 3.6a1.6 1.6 0 0 1-2-.1l-5.4-4.8z" fill="{c}"/><path d="M10.6 8.8l-2.4 2.4 1.4 1.2 2.6-1.8 3 2.6" fill="none" stroke="#fff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  agent: {
    colour: '#0b7285',
    title: 'کارشناس مرکز تماس',
    badge:
      '<path d="M5.4 14v-2a6.6 6.6 0 0 1 13.2 0v2" fill="none" stroke="{c}" stroke-width="2.6" stroke-linecap="round"/><rect x="3.6" y="12.3" width="4.6" height="6.6" rx="1.9" fill="{c}"/><rect x="15.8" y="12.3" width="4.6" height="6.6" rx="1.9" fill="{c}"/>',
  },
}

/** The words of a kind, for a picture's accessible name: «دسترسی کامل», «مدیر سیستم» …; '' for none. */
export const figureKindTitle = (kind: FigureKind) => (kind ? KINDS[kind].title : '')

const NO_KIND = '#64748b'

/** A colour lighter (amount > 0) or darker (< 0), as #rrggbb. */
export const shade = (hex: string, amount: number) => {
  const value = parseInt(hex.replace('#', ''), 16)
  return (
    '#' +
    [(value >> 16) & 255, (value >> 8) & 255, value & 255]
      .map((channel) => {
        const next = amount < 0 ? channel + channel * amount : channel + (255 - channel) * amount
        return Math.max(0, Math.min(255, Math.round(next)))
          .toString(16)
          .padStart(2, '0')
      })
      .join('')
  )
}

const person = (gender: FigureGender, background: string) => {
  const skin = '#f4d3b8'
  const body = `<path d="M10 104c0-22 17.5-35 40-35s40 13 40 35z" fill="${shade(
    background,
    -0.38
  )}"/>`
  if (gender === 'female') {
    const hair = '#3b2a24'
    return (
      body +
      `<path d="M50 21c-12 0-19.5 8.6-19.5 21.5 0 6.5.5 13.5-1.5 19.5-1 3.4-2.6 6-4.5 8 6.2 2.4 12.4 2.5 18 .2V56h15v14.2c5.6 2.3 11.8 2.2 18-.2-1.9-2-3.5-4.6-4.5-8-2-6-1.5-13-1.5-19.5C69.5 29.6 62 21 50 21z" fill="${hair}"/>` +
      '<path d="M44 57h12v11.5c0 3-2.7 5-6 5s-6-2-6-5z" fill="#e7bf9f"/>' +
      `<ellipse cx="50" cy="44" rx="14" ry="16.5" fill="${skin}"/>` +
      `<path d="M36 42.5c-1.2-11 5-18.5 14-18.5 9.4 0 15.6 7.2 14.2 18.5-2.2-6-6.6-9.6-12.8-10.6-2.6 4.6-8.4 8.4-15.4 10.6z" fill="${hair}"/>`
    )
  }
  if (gender === 'male') {
    return (
      body +
      '<path d="M43 58h14v12c0 3-3.1 5-7 5s-7-2-7-5z" fill="#e7bf9f"/>' +
      '<path d="M40 70.5l10 10.5 10-10.5c-3-1.6-6.4-2.3-10-2.3s-7 .7-10 2.3z" fill="#ffffff"/>' +
      `<path d="M48.2 74.5h3.6l1.3 13-3.1 3.2-3.1-3.2z" fill="${shade(background, -0.62)}"/>` +
      `<ellipse cx="50" cy="44" rx="15" ry="17.5" fill="${skin}"/>` +
      '<path d="M34.6 43.5C33 29 40.4 21 50.2 21c10.4 0 17.4 7.6 15.2 22.5-1.4-7.6-4.6-11.5-9.4-12.9-4.6 3.4-12.6 4.4-20.4 3.6-.6 2.6-.9 5.6-1 9.3z" fill="#2f2724"/>'
    )
  }
  return (
    '<path d="M10 104c0-22 17.5-35 40-35s40 13 40 35z" fill="#ffffff" opacity=".72"/>' +
    '<circle cx="50" cy="43" r="17" fill="#ffffff" opacity=".95"/>'
  )
}

/**
 * The figure as an SVG (pure): the circle of the kind's colour with the person in it, and - unless small - the kind's
 * badge on a white disc wholly inside the circle at its lower corner. Nothing of a name reaches the picture.
 */
export const figureSvg = (gender: FigureGender, kind: FigureKind, small = false) => {
  const background = kind ? KINDS[kind].colour : NO_KIND
  const ink = shade(background, -0.3)
  const badge =
    kind && !small
      ? `<g transform="translate(51.8 51.8) scale(1.68)"><circle cx="12" cy="12" r="10.4" fill="#fff" stroke="${ink}" stroke-width="1.2"/>${KINDS[
          kind
        ].badge.replace(/\{c\}/g, ink)}</g>`
      : ''
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">' +
    '<defs><clipPath id="f"><circle cx="50" cy="50" r="50"/></clipPath></defs>' +
    `<g clip-path="url(#f)"><rect width="100" height="100" fill="${background}"/>${person(
      gender,
      background
    )}</g>${badge}</svg>`
  )
}

const cache = new Map<string, string>()

/** The figure as an image address (a data: URL), made once for each look. */
export const figureUrl = (gender: FigureGender, kind: FigureKind, small = false) => {
  const key = `${gender}|${kind}|${small ? 1 : 0}`
  let url = cache.get(key)
  if (!url) {
    url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(figureSvg(gender, kind, small))
    cache.set(key, url)
  }
  return url
}
